// src/features/simulators/shared/components/DeviceSelector.tsx

import React from 'react';
import {
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import { useHotelDevices } from '../hooks/useHotelDevices';

interface DeviceSelectorProps {
  hotelId: string | null;
  value: string;
  onChange: (deviceId: string) => void;
  disabled?: boolean;
  deviceType?: string;  // Filter by device type (e.g., "Kiosk", "Dispenser")
  channel?: string;   // Filter by channel (e.g., "Kiosk", "Online")
}

/**
 * Device selector component
 * Shows a dropdown of existing devices or allows creating a new one
 * Supports filtering by deviceType and channel
 * 
 * @example
 * // For Kiosk Simulator - only show Kiosk devices
 * <DeviceSelector
 *   hotelId={hotelId}
 *   value={deviceId}
 *   onChange={setDeviceId}
 *   deviceType="Kiosk"
 *   channel="Kiosk"
 * />
 */
export const DeviceSelector: React.FC<DeviceSelectorProps> = ({
  hotelId,
  value,
  onChange,
  disabled = false,
  deviceType,
  channel,
}) => {
  const translate = useTranslate();
  const { devices, isLoading, error } = useHotelDevices(hotelId, {
    deviceType,
    channel,
  });

  
  const getDeviceStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      case 'error':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (!hotelId) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        {translate('resources.simulators.config.select_hotel_first')}
      </Typography>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {translate('resources.simulators.config.error_loading_devices')}: {error}
      </Alert>
    );
  }

  return (
    <Box>
       <FormControl fullWidth disabled={disabled || isLoading}>
          <FormLabel>{translate('resources.simulators.config.select_device')}</FormLabel>
          <Select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              {isLoading 
                ? translate('resources.simulators.config.loading_devices')
                : devices.length === 0
                  ? translate('resources.simulators.config.no_devices')
                  : translate('resources.simulators.config.select_device_placeholder')
              }
            </MenuItem>
            
            {devices.map((device) => (
              <MenuItem key={device.id} value={device.installationCode}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Box sx={{ flex: 1 }}>
                    <strong>{device.installationCode}</strong>
                    <Box component="span" sx={{ fontSize: '0.85em', color: 'text.secondary', ml: 1 }}>
                      ({device.deviceType} - {device.channel})
                    </Box>
                  </Box>
                  <Chip
                    label={device.status}
                    size="small"
                    color={getDeviceStatusColor(device.status)}
                  />
                </Box>
              </MenuItem>
            ))}
          </Select>
          
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          
          {!isLoading && devices.length === 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {translate('resources.simulators.config.no_devices_in_hotel')}
            </Alert>
          )}
        </FormControl>
    
    </Box>
  );
};